import { Injectable, signal } from '@angular/core';
import { environment } from '../../environments/environment';

declare const grecaptcha: {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
  render: (container: string | HTMLElement, params: {
    sitekey: string;
    callback: (token: string) => void;
    'expired-callback'?: () => void;
    theme?: string;
    size?: string;
  }) => number;
  reset: (widgetId: number) => void;
};

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private readonly _isEnabled = signal(false);
  readonly isEnabled = this._isEnabled.asReadonly();

  private v2WidgetId: number | null = null;

  initialize(): void {
    if (!environment.recaptchaSiteKey) {
      console.warn('[RecaptchaService] Site key no configurada. reCAPTCHA deshabilitado.');
      return;
    }

    if (typeof grecaptcha !== 'undefined') {
      grecaptcha.ready(() => {
        this._isEnabled.set(true);
      });
    }
  }

  async executeAction(action: string): Promise<string | null> {
    if (!this._isEnabled()) {
      return null;
    }

    try {
      return await grecaptcha.execute(environment.recaptchaSiteKey, { action });
    } catch (error) {
      console.error('[RecaptchaService] Error al ejecutar reCAPTCHA v3:', error);
      return null;
    }
  }

  renderV2(container: HTMLElement): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!environment.recaptchaV2SiteKey) {
        reject('reCAPTCHA v2 site key no configurada');
        return;
      }

      try {
        this.v2WidgetId = grecaptcha.render(container, {
          sitekey: environment.recaptchaV2SiteKey,
          callback: (token: string) => resolve(token),
          'expired-callback': () => reject('reCAPTCHA expirado')
        });
      } catch (error) {
        console.error('[RecaptchaService] Error al renderizar reCAPTCHA v2:', error);
        reject(error);
      }
    });
  }

  resetV2(): void {
    if (this.v2WidgetId !== null) {
      try {
        grecaptcha.reset(this.v2WidgetId);
      } catch { /* widget ya no existe */ }
      this.v2WidgetId = null;
    }
  }
}
